import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C51_PAGE} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C51ControlsViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.7rem',
    py: 0.75,
    px: 0.4,
    lineHeight: 1.25,
    verticalAlign: 'bottom' as const,
    whiteSpace: 'pre-line' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.4,
    px: 0.4,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

function CellInput({
    value,
    onChange,
    multiline,
    align = 'left',
    rows = 3,
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    align?: 'left' | 'center';
    rows?: number;
}) {
    if (multiline) {
        return (
            <Box
                component="textarea"
                value={value}
                rows={rows}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                sx={[
                    nativeBaseSx,
                    {textAlign: align, height: rows * 18 + 12, minHeight: rows * 18 + 12, resize: 'none'},
                ] as SxProps<Theme>}
            />
        );
    }
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={[nativeBaseSx, {textAlign: align, height: 32}] as SxProps<Theme>}
        />
    );
}

/** Excel C5.1 — Evaluate Design & Implementation of Control Activities */
export default function C51ControlsView({header, values, onChange}: C51ControlsViewProps) {
    const f = (id: string, k: string) => {
        const direct = values[`${id}.${k}`];
        if (direct != null && String(direct).trim() !== '') return direct;
        // migrate old defaults that stored design evaluation in comments
        if (k === 'design') return values[`${id}.comments`] ?? '';
        return '';
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C5.1"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1, mt: 0.5}}
            >
                {C51_PAGE.title}
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', md: '72px 1fr 220px'},
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 1.5,
                    '& > *': {
                        borderRight: {md: '1px solid'},
                        borderColor: 'divider',
                        p: 1,
                    },
                    '& > *:last-of-type': {borderRight: 0},
                }}
            >
                <Typography variant="body2" fontWeight={700} sx={{alignSelf: 'start'}}>
                    {C51_PAGE.noteLabel}
                </Typography>
                <Typography variant="body2" sx={{lineHeight: 1.4, whiteSpace: 'pre-line'}}>
                    {C51_PAGE.noteLeft}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{lineHeight: 1.4, fontWeight: 600, whiteSpace: 'pre-line'}}
                >
                    {C51_PAGE.noteRight}
                </Typography>
            </Box>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 1200,
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                    }}
                >
                    <colgroup>
                        <col style={{width: '3%'}}/>
                        <col style={{width: '10%'}}/>
                        <col style={{width: '20%'}}/>
                        <col style={{width: '18%'}}/>
                        <col style={{width: '20%'}}/>
                        <col style={{width: '8%'}}/>
                        <col style={{width: '9%'}}/>
                        <col style={{width: '12%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} align="center">{C51_PAGE.columns.ref}</TableCell>
                            <TableCell sx={headSx}>{C51_PAGE.columns.area}</TableCell>
                            <TableCell sx={headSx}>{C51_PAGE.columns.desc}</TableCell>
                            <TableCell sx={headSx}>{C51_PAGE.columns.design}</TableCell>
                            <TableCell sx={headSx}>{C51_PAGE.columns.impl}</TableCell>
                            <TableCell sx={headSx} align="center">{C51_PAGE.columns.key}</TableCell>
                            <TableCell sx={headSx} align="center">{C51_PAGE.columns.further}</TableCell>
                            <TableCell sx={headSx} align="center">{C51_PAGE.columns.ice}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C51_PAGE.rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" fontWeight={600} align="center">
                                        {row.num}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'area')}
                                        onChange={(v) => onChange(`${row.id}.area`, v)}
                                        multiline
                                        rows={3}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'desc')}
                                        onChange={(v) => onChange(`${row.id}.desc`, v)}
                                        multiline
                                        rows={4}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'design')}
                                        onChange={(v) => onChange(`${row.id}.design`, v)}
                                        multiline
                                        rows={4}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'impl')}
                                        onChange={(v) => onChange(`${row.id}.impl`, v)}
                                        multiline
                                        rows={4}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'key')}
                                        onChange={(v) => onChange(`${row.id}.key`, v)}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'further')}
                                        onChange={(v) => onChange(`${row.id}.further`, v)}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'ice')}
                                        onChange={(v) => onChange(`${row.id}.ice`, v)}
                                        align="center"
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
